import { BarChart3, BookOpen, Download, LineChart, Table2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useAcmRecords } from '../hooks/useKpi.js';
import { useBreakthrough } from '../hooks/useBreakthrough.js';
import { buildCsv, downloadCsv } from '../lib/csv.js';
import type { AcmBlock, AcmColumn, AcmRecord } from '../types/kpi.js';
import type { Granularity } from '../lib/kpi-aggregation.js';
import { KpiChartView } from '../components/KpiChartView.js';
import { KpiDefinitionView } from '../components/KpiDefinitionView.js';
import { DateRangeFilter } from '../components/chart/DateRangeFilter.js';
import { BreakthroughChart } from '../components/breakthrough/BreakthroughChart.js';

/** @graph-connects none */
type ViewMode = 'table' | 'chart' | 'definition';

/** acm_records の集計起点（JST）。日付検索の下限・既定の from。 @graph-connects none */
const SERIES_START = '2026-04-15';

/** ブロック見出しの背景（2段ヘッダの上段グループ色）。 @graph-connects none */
const BLOCK_TINT: Record<AcmBlock, string> = {
  会員数: 'var(--color-accent-primary)',
  スタイリング: 'var(--color-text-secondary)',
  'チケット・返却': 'var(--color-text-muted)',
  集客: 'var(--color-accent-secondary)',
};

interface BlockGroup {
  block: AcmBlock;
  span: number;
}

/** 列定義を連続ブロックごとにまとめ、2段ヘッダ上段の colSpan を算出する。 @graph-connects none */
function toBlockGroups(columns: AcmColumn[]): BlockGroup[] {
  const groups: BlockGroup[] = [];
  for (const col of columns) {
    const last = groups.length > 0 ? groups[groups.length - 1] : undefined;
    if (last && last.block === col.block) {
      last.span += 1;
    } else {
      groups.push({ block: col.block, span: 1 });
    }
  }
  return groups;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次KPI（acm_records）ページ。表/グラフ/定義の3モード切替で4ブロック25指標を表示する。グラフモードでは粒度（日/週/月/四半期/年）切替と折れ線チャートで推移を可視化する。定義モードでは各指標のビジネス定義と技術定義（ソースDB・テーブル・SQLロジック）を確認できる。
 * @graph-connects useAcmRecords [calls] acm_records 取得フック
 * @graph-connects useBreakthrough [calls] 1〜3ヶ月目突破率取得フック
 * @graph-connects BreakthroughChart [calls] 1〜3ヶ月目突破率チャートコンポーネント
 * @graph-connects buildCsv [calls] CSV生成
 * @graph-connects downloadCsv [calls] CSVダウンロード
 * @graph-connects KpiChartView [calls] グラフ表示コンポーネント
 * @graph-connects KpiDefinitionView [calls] 指標定義ビューアコンポーネント
 */
export function KpiPage() {
  const { t } = useTranslation();
  const [from, setFrom] = useState(SERIES_START);
  const [to, setTo] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [granularity, setGranularity] = useState<Granularity>('daily');
  const query = useAcmRecords(from || undefined, to || undefined);
  const breakthroughGranularity = granularity === 'daily' ? 'weekly' : granularity;
  const breakthroughQuery = useBreakthrough(
    from || undefined,
    to || undefined,
    breakthroughGranularity,
  );

  const columns = query.data?.columns ?? [];
  const rows = useMemo<AcmRecord[]>(
    () => [...(query.data?.rows ?? [])].sort((a, b) => (a.date < b.date ? 1 : -1)),
    [query.data],
  );
  const ascRows = useMemo<AcmRecord[]>(
    () => [...(query.data?.rows ?? [])].sort((a, b) => (a.date < b.date ? -1 : 1)),
    [query.data],
  );
  const blockGroups = useMemo(() => toBlockGroups(columns), [columns]);

  const handleExport = (): void => {
    const csv = buildCsv(columns, rows);
    downloadCsv(`acm_records_${from || SERIES_START}_${to || rows[0]?.date || 'latest'}.csv`, csv);
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-8">
      <div className="mb-1 font-mono text-xs tracking-widest text-[var(--color-accent-primary)] uppercase">
        KPI
      </div>
      <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">{t('kpi.title')}</h1>
      <p className="mt-2 text-sm text-[var(--color-text-secondary)]">{t('kpi.description')}</p>

      <Card className="mt-6 flex flex-wrap items-end gap-4 p-4">
        <DateRangeFilter
          from={from}
          to={to}
          onFromChange={setFrom}
          onToChange={setTo}
          minDate={SERIES_START}
          toLabel={t('kpi.toLabel')}
        />
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-lg border border-[var(--color-border)]">
            <button
              type="button"
              onClick={() => {
                setViewMode('table');
              }}
              className={`flex items-center gap-1 rounded-l-lg px-3 py-2 text-xs transition-colors ${
                viewMode === 'table'
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
              }`}
              aria-label={t('kpi.tableMode')}
            >
              <Table2 className="h-3.5 w-3.5" aria-hidden="true" />
              {t('kpi.table')}
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('chart');
              }}
              className={`flex items-center gap-1 border-l border-[var(--color-border)] px-3 py-2 text-xs transition-colors ${
                viewMode === 'chart'
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
              }`}
              aria-label={t('kpi.chartMode')}
            >
              <LineChart className="h-3.5 w-3.5" aria-hidden="true" />
              {t('kpi.chart')}
            </button>
            <button
              type="button"
              onClick={() => {
                setViewMode('definition');
              }}
              className={`flex items-center gap-1 rounded-r-lg border-l border-[var(--color-border)] px-3 py-2 text-xs transition-colors ${
                viewMode === 'definition'
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
              }`}
              aria-label={t('kpi.definitionMode')}
            >
              <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />
              {t('kpi.definition')}
            </button>
          </div>
          <Button variant="secondary" onClick={handleExport} disabled={rows.length === 0}>
            <Download className="mr-1.5 h-4 w-4" aria-hidden="true" />
            {t('common.csvExport')}
          </Button>
        </div>
      </Card>

      {viewMode === 'definition' ? (
        <KpiDefinitionView columns={columns} />
      ) : query.isLoading ? (
        <p className="mt-8 text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : query.isError ? (
        <Card className="mt-6 p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">{t('kpi.fetchError')}</p>
        </Card>
      ) : rows.length === 0 ? (
        <Card className="mt-6 p-8 text-center">
          <BarChart3
            className="mx-auto h-8 w-8 text-[var(--color-text-muted)]"
            aria-hidden="true"
          />
          <p className="mt-3 text-sm text-[var(--color-text-secondary)]">{t('kpi.noData')}</p>
        </Card>
      ) : viewMode === 'chart' ? (
        <>
          <KpiChartView
            columns={columns}
            rows={ascRows}
            granularity={granularity}
            onGranularityChange={setGranularity}
          />
          {!breakthroughQuery.isLoading && !breakthroughQuery.isError && (
            <div className="mt-6">
              <BreakthroughChart
                data={breakthroughQuery.confirmedRows}
                evaluationDays={breakthroughQuery.evaluationDays}
                granularity={breakthroughGranularity}
              />
            </div>
          )}
          {query.data?.snapshotAt && (
            <p className="mt-3 text-xs text-[var(--color-text-muted)]">
              {t('common.lastUpdated')}: {query.data.snapshotAt}
            </p>
          )}
        </>
      ) : (
        <>
          <div className="mt-6 overflow-x-auto rounded-lg border border-[var(--color-border)]">
            <table className="min-w-full border-collapse text-right text-sm">
              <thead>
                <tr className="bg-[var(--color-bg-subtle,var(--color-bg-base))]">
                  <th
                    rowSpan={2}
                    className="sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-2 text-left font-semibold text-[var(--color-text-secondary)]"
                  >
                    {t('common.date')}
                  </th>
                  {blockGroups.map((group) => (
                    <th
                      key={group.block}
                      colSpan={group.span}
                      className="border-b border-l border-[var(--color-border)] px-3 py-1.5 text-center text-xs font-semibold tracking-wide"
                      style={{ color: BLOCK_TINT[group.block] }}
                    >
                      {group.block}
                    </th>
                  ))}
                </tr>
                <tr className="bg-[var(--color-bg-base)]">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="border-b border-[var(--color-border)] px-3 py-2 align-bottom text-xs font-medium whitespace-nowrap text-[var(--color-text-secondary)]"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.date} className="hover:bg-[var(--color-bg-base)]">
                    <td className="sticky left-0 z-10 border-b border-[var(--color-border)] bg-[var(--color-bg-base)] px-3 py-1.5 text-left font-mono text-[var(--color-text-primary)]">
                      {row.date}
                    </td>
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className="border-b border-[var(--color-border)] px-3 py-1.5 tabular-nums text-[var(--color-text-primary)]"
                      >
                        {row[col.key].toLocaleString('ja-JP')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-[var(--color-text-muted)]">
            {t('kpi.rowCount', { days: String(rows.length) })}
            {query.data?.snapshotAt
              ? ` ・ ${t('common.lastUpdated')}: ${query.data.snapshotAt}`
              : ''}
          </p>
        </>
      )}
    </div>
  );
}
